import { Checkbox, Spin, Radio, Form, Tooltip } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import useCrashDetectionDetailPageHook from './CrashDetectionDetailPage.hook'
import {
  Container,
  LoadingContainer,
  Card,
  Title,
  SectionTitle,
  InfoGrid,
  Field,
  Label,
  Value,
  MapWrapper,
  DetailLayout,
  LeftContent,
  RightContent,
  Header,
  SubHeader,
  TitleWrapper,
  StatusBadge,
  Button,
  ButtonSuccessStyle,
  CoordinatesValue,
  StyledCopyIcon,
  SupportingText,
  SupportingTextBold,
  RightField,
  RightLabel,
  RequiredAsterisk,
  DetailActionWrapper,
  FullWidthSelect,
  FullWidthInput,
  FullWidthTextArea,
  StyledRadioGroup,
  BackIcon,
} from './styles'
import { CrashContactStatus, CrashIncidentStatus } from '@/services/crash-detection/types'
import Map from '@/components/common/Map'
import CrashDetectionLockedModal from '@/components/common/CrashDetectionLockedModal'
import { IMAGE_URL } from '@/constant/IMAGE'

// Contact statuses that enable เลขรับแจ้ง / หมายเหตุ text inputs
const TEXT_FIELDS_ENABLED_STATUSES: CrashContactStatus[] = [
  CrashContactStatus.CONTACTED,
  CrashContactStatus.UNREACHABLE,
  CrashContactStatus.NOT_CONTACTED,
]

const CrashDetectionDetailPage = ({ mode = 'view' }: { mode?: 'view' | 'edit' }) => {
  const {
    form,
    data,
    isLoading,
    isUpdating,
    isViewMode,
    statusBadge,
    hasCoordinates,
    formattedIncidentDate,
    isCrashCase,
    showEditButton,
    handleContactStatusChange,
    handleIncidentStatusChange,
    handleSave,
    handleCancel,
    handleNavigateBack,
    handleNavigateToEdit,
    handleCopyCoordinates,
    isFormDirty,
  } = useCrashDetectionDetailPageHook(mode)

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    )
  }

  if (!data) return null

  return (
    <Container>
      <Header>
        <SubHeader>
          <TitleWrapper>
            <BackIcon
              onClick={handleNavigateBack}
              aria-label="ย้อนกลับไปหน้ารายการ"
              role="button"
            />
            <Title>
              รายละเอียดการแจ้งเตือนการชน
              <StatusBadge bg={statusBadge.bg} color={statusBadge.color}>
                {statusBadge.label}
              </StatusBadge>
            </Title>
          </TitleWrapper>
        </SubHeader>
      </Header>

      <DetailLayout>
        <LeftContent>
          <Card>
            <SectionTitle>ข้อมูลเหตุ</SectionTitle>
            <InfoGrid>
              <Field>
                <Label>เลขที่กรมธรรม์</Label>
                <Value>{data.policyNumber || '-'}</Value>
              </Field>
              <Field>
                <Label>ชื่อ-นามสกุล</Label>
                <Value>{data.fullName || '-'}</Value>
              </Field>
              <Field>
                <Label>เบอร์โทรศัพท์</Label>
                <Value>{data.emergencyContactNo || '-'}</Value>
              </Field>
              <Field>
                <Label>ทะเบียนรถ</Label>
                <Value>{data.licensePlate || '-'}</Value>
              </Field>
              <Field>
                <Label>วันและเวลาที่เกิดเหตุ</Label>
                <Value>{formattedIncidentDate}</Value>
              </Field>
              <Field>
                <Label>พิกัดที่เกิดเหตุ (Lat, Long)</Label>
                {hasCoordinates ? (
                  <CoordinatesValue>
                    <span>
                      {data.latitude},{data.longitude}
                    </span>
                    <Tooltip title="คลิกเพื่อคัดลอก">
                      <StyledCopyIcon
                        onClick={handleCopyCoordinates}
                        role="button"
                        aria-label="คัดลอกพิกัด"
                      >
                        <img src={IMAGE_URL.copyIcon} alt="copy" />
                      </StyledCopyIcon>
                    </Tooltip>
                  </CoordinatesValue>
                ) : (
                  <Value>-</Value>
                )}
              </Field>
            </InfoGrid>
            <br />
            <SectionTitle>ตำแหน่งที่เกิดเหตุ</SectionTitle>
            <MapWrapper>
              {hasCoordinates ? (
                <Map
                  height="450px"
                  locations={[
                    {
                      position: [data.latitude!, data.longitude!],
                      iconUrl: IMAGE_URL.redPinMapMarker,
                      iconSize: [24, 36],
                      iconAnchor: [12, 36],
                    },
                  ]}
                />
              ) : (
                <Value>ไม่พบพิกัดที่เกิดเหตุ</Value>
              )}
            </MapWrapper>
          </Card>
        </LeftContent>

        <RightContent>
          <Card>
            <SectionTitle>ผลตรวจสอบการแจ้งเตือนการชน</SectionTitle>
            <SupportingText>
              <span>อัปเดตข้อมูลล่าสุดโดย</span>
              <SupportingTextBold>{data.ownerName || '-'}</SupportingTextBold>
            </SupportingText>

            {showEditButton && (
              <Button onClick={handleNavigateToEdit}>
                <EditOutlined /> แก้ไข
              </Button>
            )}

            <Form form={form} layout="vertical">
              {/* สถานะการติดต่อ */}
              <RightField>
                <RightLabel>สถานะการติดต่อ</RightLabel>
                <Form.Item name="currentContactStatus" noStyle>
                  <StyledRadioGroup
                    onChange={(e) => handleContactStatusChange(e.target.value)}
                    disabled={isViewMode}
                  >
                    <Radio value={CrashContactStatus.CONTACTED}>โทรหาแล้วติดต่อได้</Radio>
                    <Radio value={CrashContactStatus.UNREACHABLE}>ติดต่อไม่ได้</Radio>
                    <Radio value={CrashContactStatus.NOT_CONTACTED}>ยังไม่ได้ติดต่อ</Radio>
                    <Radio value={CrashContactStatus.NOT_APPLICABLE} disabled={isViewMode || isCrashCase}>
                      ไม่เข้าเงื่อนไขการติดต่อ
                    </Radio>
                  </StyledRadioGroup>
                </Form.Item>
              </RightField>

              {/* สถานะเหตุ */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.currentContactStatus !== curr.currentContactStatus
                }
              >
                {({ getFieldValue }) => {
                  const isContactSelected =
                    getFieldValue('currentContactStatus') === CrashContactStatus.CONTACTED
                  return (
                    <RightField>
                      <RightLabel>สถานะเหตุ</RightLabel>
                      <Form.Item name="currentIncidentStatus" noStyle>
                        <FullWidthSelect
                          placeholder="เลือก"
                          onChange={handleIncidentStatusChange}
                          disabled={isViewMode || !isContactSelected}
                          options={[
                            { label: 'ยังไม่ทราบสถานะ', value: CrashIncidentStatus.NO_STATUS },
                            { label: 'มีการชน', value: CrashIncidentStatus.CRASH },
                            { label: 'ไม่มีการชน', value: CrashIncidentStatus.NO_CRASH },
                          ]}
                        />
                      </Form.Item>
                    </RightField>
                  )
                }}
              </Form.Item>

              {/* ส่ง Surveyor */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.currentContactStatus !== curr.currentContactStatus ||
                  prev.currentIncidentStatus !== curr.currentIncidentStatus
                }
              >
                {({ getFieldValue }) => {
                  const isContactSelected =
                    getFieldValue('currentContactStatus') === CrashContactStatus.CONTACTED
                  const isCrashSelected =
                    getFieldValue('currentIncidentStatus') === CrashIncidentStatus.CRASH
                  return (
                    <RightField>
                      <Form.Item name="isSendSurveyor" valuePropName="checked" noStyle>
                        <Checkbox disabled={isViewMode || !isContactSelected || !isCrashSelected}>
                          ส่ง Surveyor ออกตรวจสอบ
                        </Checkbox>
                      </Form.Item>
                    </RightField>
                  )
                }}
              </Form.Item>

              {/* เลขรับแจ้ง */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.currentContactStatus !== curr.currentContactStatus ||
                  prev.currentIncidentStatus !== curr.currentIncidentStatus
                }
              >
                {({ getFieldValue }) => {
                  const isCrashSelected =
                    getFieldValue('currentIncidentStatus') === CrashIncidentStatus.CRASH
                  const isNotApplicable =
                    getFieldValue('currentContactStatus') === CrashContactStatus.NOT_APPLICABLE
                  return (
                    <RightField>
                      <RightLabel>เลขรับแจ้ง (ถ้ามี)</RightLabel>
                      <Form.Item name="notifyNumber" noStyle>
                        <FullWidthInput
                          placeholder="ระบุ"
                          disabled={isViewMode || isNotApplicable || !isCrashSelected}
                        />
                      </Form.Item>
                    </RightField>
                  )
                }}
              </Form.Item>

              {/* หมายเหตุ */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.currentContactStatus !== curr.currentContactStatus ||
                  prev.currentIncidentStatus !== curr.currentIncidentStatus
                }
              >
                {({ getFieldValue }) => {
                  const contactStatus = getFieldValue('currentContactStatus')
                  const incidentStatus = getFieldValue('currentIncidentStatus')
                  const isTextFieldsEnabled = TEXT_FIELDS_ENABLED_STATUSES.includes(contactStatus)
                  const isRemarkRequired =
                    contactStatus === CrashContactStatus.CONTACTED &&
                    incidentStatus === CrashIncidentStatus.NO_STATUS

                  return (
                    <RightField>
                      <RightLabel>
                        หมายเหตุ{' '}
                        {isRemarkRequired ? <RequiredAsterisk>*</RequiredAsterisk> : '(ถ้ามี)'}
                      </RightLabel>
                      <Form.Item
                        name="note"
                        noStyle
                        rules={
                          isRemarkRequired && !isViewMode
                            ? [
                                {
                                  required: true,
                                  whitespace: true,
                                  message:
                                    '* กรุณาระบุหมายเหตุในกรณีที่ติดต่อได้แต่ยังไม่ทราบสถานะเหตุ',
                                },
                              ]
                            : []
                        }
                      >
                        <FullWidthTextArea
                          rows={4}
                          placeholder="ระบุ"
                          disabled={isViewMode}
                        />
                      </Form.Item>
                    </RightField>
                  )
                }}
              </Form.Item>

              {!isViewMode && (
                <DetailActionWrapper>
                  <Button onClick={handleCancel}>ยกเลิก</Button>
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const dirty = isFormDirty(form.getFieldsValue())
                      const hasErrors = form
                        .getFieldsError()
                        .some(({ errors }) => errors.length > 0)
                      return (
                        <ButtonSuccessStyle
                          disabled={!dirty || hasErrors || isUpdating}
                          onClick={handleSave}
                        >
                          บันทึก
                        </ButtonSuccessStyle>
                      )
                    }}
                  </Form.Item>
                </DetailActionWrapper>
              )}
            </Form>
          </Card>
        </RightContent>
      </DetailLayout>
      <CrashDetectionLockedModal />
    </Container>
  )
}

export default CrashDetectionDetailPage
